import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Check, FileIcon } from 'lucide-react';
import { uploadFile } from '../../services/storage';
import { FileType } from '../../types/media';

interface DropZoneProps {
  factoryId: string;
  onUploadComplete: () => void;
  fileType: FileType;
}

interface FileToUpload {
  file: File;
  fileType: FileType;
}

const DropZone: React.FC<DropZoneProps> = ({ factoryId, onUploadComplete, fileType }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  const [uploadSuccess, setUploadSuccess] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<FileToUpload[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  const processNextFile = useCallback(async () => {
    if (currentFileIndex >= pendingFiles.length) {
      setPendingFiles([]);
      setCurrentFileIndex(0);
      setShowConfirmDialog(false);
      onUploadComplete();
      return;
    }

    const fileToUpload = pendingFiles[currentFileIndex];
    const file = fileToUpload.file;
    
    try {
      setUploading(true);
      setShowConfirmDialog(false);
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

      await uploadFile(file, factoryId, fileToUpload.fileType, (progress) => {
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: progress
        }));
      });
      
      setUploadSuccess(prev => [...prev, file.name]);
      
      const nextIndex = currentFileIndex + 1;
      setCurrentFileIndex(nextIndex);
      
      if (nextIndex < pendingFiles.length) {
        setShowConfirmDialog(true);
      } else {
        setUploading(false);
        setPendingFiles([]);
        setCurrentFileIndex(0);
        setShowConfirmDialog(false);
        
        setTimeout(() => {
          setUploadProgress({});
          setUploadErrors({});
          setUploadSuccess([]);
          onUploadComplete();
        }, 2000);
      }
    } catch (error) {
      console.error(`Error uploading ${file.name}:`, error);
      setUploadErrors(prev => ({
        ...prev,
        [file.name]: error instanceof Error ? error.message : 'Échec du téléchargement. Veuillez réessayer.'
      }));
      
      const nextIndex = currentFileIndex + 1;
      setCurrentFileIndex(nextIndex);
      setShowConfirmDialog(false);
      
      if (nextIndex < pendingFiles.length) {
        setTimeout(() => setShowConfirmDialog(true), 100);
      } else {
        setUploading(false);
        setPendingFiles([]);
        setCurrentFileIndex(0);
      }
    }
  }, [currentFileIndex, pendingFiles, factoryId, onUploadComplete, fileType]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const validFiles = acceptedFiles.filter(file => 
      file.type === 'video/mp4' || 
      file.type === 'audio/mpeg' ||
      file.type === 'text/plain' ||
      file.type === 'application/pdf' ||
      file.type === 'application/msword' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    );
    
    if (validFiles.length === 0) {
      alert('Seuls les fichiers MP4, MP3, TXT, PDF, et DOC sont acceptés.');
      return;
    }

    setPendingFiles(validFiles.map(file => ({ 
      file,
      fileType
    })));
    setCurrentFileIndex(0);
    setShowConfirmDialog(true);
  }, [fileType]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    onDrop,
    accept: {
      'video/mp4': ['.mp4'],
      'audio/mpeg': ['.mp3'],
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    disabled: uploading || showConfirmDialog,
    maxSize: 524288000 // 500MB max
  });

  return (
    <div className="my-6">
      <div
        {...getRootProps()}
        className={`dropzone p-6 flex flex-col items-center justify-center cursor-pointer transition-all ${
          isDragActive ? 'bg-gray-50 dark:bg-gray-800' : ''
        } ${isDragAccept ? 'border-primary bg-green-50 dark:bg-green-900/20' : ''} ${
          isDragReject ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : ''
        } ${uploading || showConfirmDialog ? 'opacity-75 pointer-events-none' : ''}`}
      >
        <input {...getInputProps()} />
        
        <Upload className="w-12 h-12 text-primary mb-4" />
        
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Déposer les fichiers ici
        </h3>
        
        <p className="text-sm text-center text-gray-600 dark:text-gray-300 mb-2">
          Glisser et déposer des fichiers MP4, MP3, TXT, PDF, ou DOC
        </p>
        
        <p className="text-xs text-center text-gray-500 dark:text-gray-400">
          ou cliquez pour sélectionner des fichiers
        </p>
      </div>

      {showConfirmDialog && currentFileIndex < pendingFiles.length && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Confirmer le téléchargement
              </h3>
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setPendingFiles([]);
                  setCurrentFileIndex(0);
                }}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              Voulez-vous télécharger ce fichier ?
            </p>
            
            <p className="text-sm font-medium text-gray-900 dark:text-white mb-6">
              {pendingFiles[currentFileIndex].file.name}
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setPendingFiles([]);
                  setCurrentFileIndex(0);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Non
              </button>
              <button
                onClick={() => processNextFile()}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                Oui
              </button>
            </div>
          </div>
        </div>
      )}

      {Object.keys(uploadProgress).length > 0 && (
        <div className="mt-6 space-y-4">
          <h4 className="font-medium text-gray-900 dark:text-white">Téléchargement en cours</h4>
          
          {Object.entries(uploadProgress).map(([fileName, progress]) => (
            <div key={fileName} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center">
                <FileIcon className="w-5 h-5 text-primary mr-3" />
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{fileName}</p>
                  
                  {uploadErrors[fileName] ? (
                    <p className="text-xs text-red-500">{uploadErrors[fileName]}</p>
                  ) : uploadSuccess.includes(fileName) ? (
                    <div className="flex items-center text-xs text-green-500">
                      <Check className="w-3 h-3 mr-1" />
                      Téléchargement terminé
                    </div>
                  ) : (
                    <>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full mt-2">
                        <div 
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {Math.round(progress)}% terminé
                      </p>
                    </>
                  )}
                </div>
                
                {!uploadSuccess.includes(fileName) && !uploadErrors[fileName] && (
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    {Math.round(progress)}%
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DropZone;